import { useEffect, useMemo, useRef, useState } from 'react';
import type { Category, Channel, ScheduleChannel } from '../types';

interface Props {
  channels: Channel[];
  categories: Category[];
  onPlay: (channel: Channel) => void;
  onLoadSchedules: (date: Date) => Promise<ScheduleChannel[]>;
}

const PIXELS_PER_MINUTE = 4;

function minutes(value: string) {
  const [hours, mins] = value.split(':').map(Number);
  return hours * 60 + mins;
}

function dayLabel(date: Date, index: number) {
  if (index === 0) return 'Hoje';
  return new Intl.DateTimeFormat('pt-BR', { weekday: 'short', day: '2-digit' })
    .format(date).replace('.', '');
}

export function GuideView({ channels, categories, onPlay, onLoadSchedules }: Props) {
  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + index);
    return date;
  }), []);
  const [dayIndex, setDayIndex] = useState(0);
  const [category, setCategory] = useState('ALL');
  const [scheduleMap, setScheduleMap] = useState(() => new Map(channels.map(channel => [channel.id, channel.schedules || []])));
  const [loading, setLoading] = useState(false);
  const viewport = useRef<HTMLDivElement>(null);
  const visible = category === 'ALL' ? channels : channels.filter(channel => channel.category.includes(category));

  useEffect(() => {
    if (dayIndex === 0) return;
    let active = true;
    void onLoadSchedules(days[dayIndex]).then(result => {
      if (active) setScheduleMap(new Map(result.map(channel => [channel.id, channel.schedules || []])));
    }).catch(() => {
      if (active) setScheduleMap(new Map());
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [channels, dayIndex, days, onLoadSchedules]);

  useEffect(() => {
    const now = new Date();
    const currentMinute = now.getHours() * 60 + now.getMinutes();
    if (viewport.current) viewport.current.scrollLeft = Math.max(0, (dayIndex ? 7 * 60 : currentMinute - 45) * PIXELS_PER_MINUTE);
  }, [dayIndex, category]);

  const now = new Date();
  const currentMinute = now.getHours() * 60 + now.getMinutes();
  const activeScheduleMap = dayIndex === 0
    ? new Map(channels.map(channel => [channel.id, channel.schedules || []]))
    : scheduleMap;
  const selectDay = (index: number) => {
    setLoading(index !== 0);
    setDayIndex(index);
  };
  return <>
    <header className="topbar topbar--compact"><div><span className="eyebrow">O QUE ESTÁ PASSANDO</span><h1>Guia de programação</h1></div></header>
    <div className="guide-controls">
      <div className="guide-days" data-focus-zone="guide-days">{days.map((day, index) => <button
        key={day.toISOString()} data-focusable="true" data-focus-zone="guide-days"
        data-content-autofocus={index === 0 ? 'true' : undefined}
        data-focus-key={`guide-day-${index}`} className={dayIndex === index ? 'active' : ''}
        onClick={() => selectDay(index)}>{dayLabel(day, index)}</button>)}</div>
      <button data-focusable="true" data-focus-zone="guide-days" className="guide-now" onClick={() => {
        selectDay(0);
        if (viewport.current) viewport.current.scrollLeft = Math.max(0, (currentMinute - 45) * PIXELS_PER_MINUTE);
      }}>Agora</button>
    </div>
    <div className="category-row category-row--guide" aria-label="Categorias">
      {categories.slice(0, 8).map(item => <button key={item.code} data-focusable="true"
        data-focus-zone="guide-categories" className={category === item.code ? 'active' : ''}
        onClick={() => setCategory(item.code)}>{item.name}</button>)}
    </div>
    <div className={`guide-timeline ${loading ? 'is-loading' : ''}`} ref={viewport} data-testid="guide-timeline">
      <div className="guide-timeline__canvas">
        <div className="guide-hours"><span className="guide-hours__corner">Canal</span>{Array.from({ length: 24 }, (_, hour) => <span key={hour} style={{ left: `${hour * 60 * PIXELS_PER_MINUTE}px` }}>{String(hour).padStart(2, '0')}:00</span>)}</div>
        {dayIndex === 0 && <i className="guide-current-time" style={{ left: `${currentMinute * PIXELS_PER_MINUTE}px` }} />}
        {visible.map(channel => {
          const schedules = activeScheduleMap.get(channel.id) || [];
          return <div className="guide-timeline__row" key={channel.id} data-testid={`guide-channel-${channel.channel_number}`}>
            <button className="guide-channel" data-focusable="true" data-focus-zone={`guide-${channel.id}`} onClick={() => onPlay(channel)}>
              <b>{channel.channel_number}</b><img src={channel.image} alt="" /><span>{channel.name}</span>
            </button>
            <div className="guide-programs">{schedules.length ? schedules.map(program => {
              const start = minutes(program.time_start);
              const width = Math.max(90, (minutes(program.time_end) - start) * PIXELS_PER_MINUTE);
              return <button key={program.id} data-focusable="true" data-focus-zone={`guide-${channel.id}`}
                data-focus-key={`guide-program-${program.id}`} style={{ left: `${start * PIXELS_PER_MINUTE}px`, width: `${width}px` }}
                onClick={() => onPlay(channel)}><strong>{program.name}</strong><small>{program.time_start.slice(0, 5)}–{program.time_end.slice(0, 5)}</small></button>;
            }) : <button className="guide-programs__empty" data-focusable="true" data-focus-zone={`guide-${channel.id}`} onClick={() => onPlay(channel)}>Programação não informada</button>}</div>
          </div>;
        })}
      </div>
    </div>
  </>;
}
